// src/controllers/useCartPageController.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL, BASE_IMAGE_URL } from "../utils/api";
import {
  money,
  pickBestOffer,
  ACCESSORY_FEE,
  OUTSIDE_RIYADH_SURCHARGE,
} from "../utils/cart";
import Swal from "sweetalert2";

const SA_NAME = "السعودية";

// Small helpers to keep popups consistent
const swalError = (title, text) =>
  Swal.fire({
    icon: "error",
    title,
    text,
    confirmButtonText: "حسناً",
    confirmButtonColor: "#CAEA3E",
    iconColor: "#CAEA3E",
    customClass: { popup: "swal-dark-popup", cancelButton: "swal-cancel-btn" },
  });

const swalInfo = (title, text) =>
  Swal.fire({
    icon: "info",
    title,
    text,
    confirmButtonText: "حسناً",
    confirmButtonColor: "#CAEA3E",
    iconColor: "#CAEA3E",
    customClass: { popup: "swal-dark-popup", cancelButton: "swal-cancel-btn" },
  });

export default function useCartPageController() {
  // Items from API (mapped for UI)
  const [items, setItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);

  // Address + contact
  const [address, setAddress] = useState({
    country: "",
    cityId: "",
    cityName: "",
    hood: "",
    street: "",
    fullName: "",
    phone: "",
  });

  const [payment, setPayment] = useState("apple");
  const [coupon, setCoupon] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Auth
  const token = localStorage.getItem("token");
  const isAuthed = !!token;

  // Guest
  const [guestPhone, setGuestPhone] = useState(localStorage.getItem("guestPhone") || "");
  const [guestLoaded, setGuestLoaded] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState("");

  // Computed flag used by the button to know if a guest can submit
  const guestReady = useMemo(() => {
    const phone = (address.phone || "").trim();
    const full = (address.fullName || "").trim();
    const hasLoc = !!(address.country && address.cityName);
    return !!(phone && full && hasLoc);
  }, [address.phone, address.fullName, address.country, address.cityName, guestPhone]);

  /* ------------------------- helpers ------------------------- */
  const mapCartToState = (cart) => {
    setCartId(cart?.id ?? null);

    setAddress((prev) => ({
      ...prev,
      country: SA_NAME,
      fullName: cart?.full_name || prev.fullName || "",
      phone: cart?.phone_number || prev.phone || "",
      street: prev.street || "",
    }));

    const mapped = (cart?.items || []).map((it, idx) => {
      const p = it.part || {};
      const qtyUi = Math.max(1, Number(it.quantity || 1));
      const base = Number(p.price || 0);
      const best = pickBestOffer(base, p.active_offers || p.activeOffers || []);
      const rawImg =
        Array.isArray(p.images) && p.images.length
          ? (p.images[0]?.image_url || p.images[0]?.url || null)
          : null;
      const img = rawImg
        ? (rawImg.startsWith("http") ? rawImg : `${BASE_IMAGE_URL}${rawImg}`)
        : `https://picsum.photos/seed/part${p.id || idx}/96/64`;

      return {
        id: it.id, // server cart-item id
        partId: p.id,
        partSku: p.sq,
        name: p.name || "قطعة",
        img,
        unitPriceOriginal: Number(p.market_price),
        unitPrice: best.finalPrice,
        unitDiscount: best.discount,
        basePrice: base,
        unitDelivery: Number(p.delivery_price || 0),
        hasAccessories: Number(p.has_accessories || 0) === 1,
        qty: qtyUi,
        offer: best.offer,
        availableQty: Number(
          p.quantity ??
          p.availableQty ??
          p.stock ??
          p.in_stock ??
          p.max_quantity ??
          NaN
        ),
      };
    });
    setItems(mapped);
  };

  // Map guest details payload (from /guest-cart/details) to UI state
  const mapGuestDetailsToState = (payload) => {
    const list = Array.isArray(payload?.items) ? payload.items : [];
    const mapped = list.map((row, idx) => {
      const p = row?.part || {};
      const base = Number(p.price || 0);
      const best = pickBestOffer(base, p.active_offers || p.activeOffers || []);
      const rawImg =
        Array.isArray(p.images) && p.images.length
          ? (p.images[0]?.image_url || p.images[0]?.url || null)
          : null;
      const img = rawImg
        ? (rawImg.startsWith("http") ? rawImg : `${BASE_IMAGE_URL}${rawImg}`)
        : `https://picsum.photos/seed/part${p.id || idx}/96/64`;

      return {
        id: Number(row.part_id), // for guests, use part_id as row id
        partId: Number(p.id),
        name: p.name || "قطعة",
        partSku: p.sq,
        img,
        unitPriceOriginal: p.market_price,
        unitPrice: best.finalPrice,
        unitDiscount: best.discount,
        basePrice: base,
        unitDelivery: Number(p.delivery_price || 0),
        hasAccessories: Number(p.has_accessories || 0) === 1,
        qty: 1, // localStorage cart is ids only
        offer: best.offer,
        availableQty: Number(
          p.quantity ??
          p.availableQty ??
          p.stock ??
          p.in_stock ??
          p.max_quantity ??
          NaN
        ),
      };
    });
    setItems(mapped);
    setCartId(null); // no server cart for guests
    setAddress((prev) => ({ ...prev, country: SA_NAME }));
  };

  const pickFirstCart = (payload) => {
    if (!payload) return null;
    if (Array.isArray(payload)) return payload[0] || null;             // authed endpoint
    if (Array.isArray(payload.carts)) return payload.carts[0] || null; // guest-by-phone (legacy)
    if (Array.isArray(payload.data)) return payload.data[0] || null;
    if (payload.cart) return payload.cart;
    return null;
  };

  /* --------------------- fetch authed carts ------------------- */
  useEffect(() => {
    if (!isAuthed) return;
    setCartLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/carts/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cart = pickFirstCart(res.data);
        if (!cart) { setItems([]); setCartId(null); return; }
        mapCartToState(cart);
      } catch (err) {
        console.error("Failed to load auth cart:", err);
        setItems([]);
        setCartId(null);
      } finally {
        setCartLoading(false);
        setGuestLoaded(true);
      }
    })();
  }, [isAuthed, token]);

  /* ---------------------- fetch guest (local) ----------------- */
  const fetchLocalGuestCart = async () => {
    try {
      setGuestLoading(true);
      setCartLoading(true);
      setGuestError("");

      const raw = localStorage.getItem("cart");
      const partIds = raw ? JSON.parse(raw) : [];

      if (!Array.isArray(partIds) || partIds.length === 0) {
        setItems([]);
        setGuestLoaded(true);
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/guest-cart/details`,
        { part_ids: partIds },
        { headers: { "Content-Type": "application/json" } }
      );

      mapGuestDetailsToState(res.data);
      setGuestLoaded(true);
    } catch (err) {
      console.error("Guest local cart fetch failed:", err);
      setGuestError("تعذر جلب السلة المحلية.");
      setItems([]);
    } finally {
      setGuestLoading(false);
      setCartLoading(false);
    }
  };

  // load local guest cart automatically when not authed
  useEffect(() => {
    if (isAuthed) return;
    fetchLocalGuestCart();
  }, [isAuthed]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------- quantity + remove handlers ------------- */
  const inc = (id) =>
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const limit =
          Number.isFinite(it.availableQty) && it.availableQty > 0
            ? it.availableQty
            : Infinity;
        const nextQty = Math.min(it.qty + 1, limit);
        return nextQty === it.qty ? it : { ...it, qty: nextQty };
      })
    );

  const dec = (id) =>
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, qty: Math.max(1, it.qty - 1) } : it))
    );

  const handleRemove = async (id) => {
    // Guest (local) → remove from localStorage["cart"]
    if (!isAuthed) {
      try {
        const raw = localStorage.getItem("cart");
        const ids = raw ? JSON.parse(raw) : [];
        const next = Array.isArray(ids) ? ids.filter((pid) => String(pid) !== String(id)) : [];
        localStorage.setItem("cart", JSON.stringify(next));
      } catch { }
      setItems((prev) => prev.filter((it) => it.id !== id));
      return;
    }

    // Authed → delete from server
    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE_URL}/cart-items/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      console.error("Failed to delete cart item:", err);
      await swalError("خطأ", "تعذّر حذف العنصر من السلة. حاول لاحقاً.");
    } finally {
      setDeletingId(null);
    }
  };

  /* --------------------------- totals ------------------------- */
  const {
    partsTotal,
    deliveryTotalRaw,
    accessoriesTotal,
    discountTotal,
    deliverySurcharge,
    grandTotal,
  } = useMemo(() => {
    const partsTotal = items.reduce((s, it) => s + it.unitPrice * it.qty, 0);

    // item-level discounts (as before)
    const itemDiscountTotal = items.reduce(
      (s, it) => s + it.unitDiscount * it.qty,
      0
    );

    // per-line delivery (capped 200 per line)
    const deliverySum = items.reduce((s, it) => {
      const line = it.unitDelivery * it.qty;
      return s + line;
    }, 0);

    // cap total delivery at 120
    const DELIVERY_CAP = 120;
    const deliveryTotalRaw = Math.min(deliverySum, DELIVERY_CAP);

    // NEW: if deliverySum > 120, add the difference to total discount
    const extraDeliveryDiscount = Math.max(0, deliverySum - DELIVERY_CAP);

    // accessories (unchanged)
    const accessoriesTotal = items.reduce(
      (s, it) => s + (it.hasAccessories ? ACCESSORY_FEE * it.qty : 0),
      0
    );

    const deliverySurcharge =
      address.cityName && address.cityName.trim() !== "الرياض"
        ? OUTSIDE_RIYADH_SURCHARGE
        : 0;

    // final totals
    const discountTotal = itemDiscountTotal + extraDeliveryDiscount;

    const grandTotal =
      partsTotal + deliveryTotalRaw + deliverySurcharge + accessoriesTotal;

    return {
      partsTotal,
      deliveryTotalRaw,
      accessoriesTotal,
      discountTotal,
      deliverySurcharge,
      grandTotal,
    };
  }, [items, address.cityName]);



  const totalQuantity = useMemo(
    () => items.reduce((s, it) => s + it.qty, 0),
    [items]
  );

  /* -------------------------- confirm ------------------------- */
  const handleConfirm = async (e) => {
  
    e?.preventDefault();
    

    // Build product_details JSON string from items
    const productDetails = JSON.stringify(
      (items || []).map((it) => {
        const referenceId = String(it.partId ?? it.id ?? "");
        const name = it.name || it.title || `Item #${it.partId ?? it.id}`;
        const sku = String(it.basePrice ?? it.partId ?? it.id ?? "");
        const quantity = Number(it.qty || 1);
        const unitPrice = Number(it.unitPrice ?? it.price ?? 0);

        return {
          reference_id: referenceId,
          type: "invoice_product",
          name,
          sku,
          quantity,
          // amount is unit price * quantity
          amount: (unitPrice * quantity).toFixed(2),
          currency: "SAR",
        };
      })
    );


    // ===== GUEST FLOW (unauthenticated) =====
    if (!isAuthed) {
      if (items.length === 0) {
        await swalInfo("تنبيه", "سلتك فارغة.");
        return;
      }
      const phone = (address.phone || guestPhone || "").trim();
      if (!phone) {
        await swalError("حقل مطلوب", "يرجى إدخال رقم الجوال.");
        return;
      }
      if (!(address.fullName || "").trim()) {
        await swalError("حقل مطلوب", "يرجى إدخال الاسم الكامل.");
        return;
      }
      if (!address.country || !address.cityName) {
        await swalError("حقل مطلوب", "يرجى اختيار الدولة والمدينة.");
        return;
      }

      try {
        setConfirming(true);

        const itemsForPayload = items.map((it) => ({
          part_id: Number(it.partId ?? it.id),
          quantity: Number(it.qty || 1),
        }));


        const payload = {
          payment_method: payment || null,
          full_name: address.fullName || "",
          last_name: "",
          phone_number: phone,
          address: [address.country, address.cityName, address.hood, address.street].filter(Boolean).join(", "),
          district: address.hood || "",
          city: address.cityName || "",
          country: address.country || "",
          street_name: address.street || "",
          total_price: Number(grandTotal),
          total_quantity: totalQuantity,
          items: itemsForPayload,
          coupon_code: coupon || undefined,
          currency: "SAR",
          // NEW: product_details as JSON string
          product_details: productDetails,
        };

        const { data } = await axios.post(`${API_BASE_URL}/guest-carts`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        const redirectUrl = data?.redirect_url || data?.payment?.redirect_url || null;

        if (redirectUrl) {
          try { localStorage.removeItem("cart"); } catch { }
          window.location.assign(redirectUrl);
          return;
        }

        await swalInfo("تم الإرسال", data?.message || "تم إرسال الطلب للمعالجة، لكن لم يتم توفير رابط الدفع.");
        return;
      } catch (err) {
        console.error("Guest confirm failed:", err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "تعذر إرسال الطلب. حاول لاحقاً.";
        await swalError("خطأ", msg);
      } finally {
        setConfirming(false);
      }
      return;
    }

    // ===== AUTHED FLOW =====
    if (!cartId) {
      await swalInfo("تنبيه", "لا توجد سلة نشطة.");
      return;
    }
    if (items.length === 0) {
      await swalInfo("تنبيه", "سلتك فارغة.");
      return;
    }

    try {
      setConfirming(true);

      const itemsForPayload = items.map((it) => ({
        id: Number(it.id),
        quantity: Number(it.qty || 1),
        price: Number((it.unitPrice || 0) * (it.qty || 1)),
      }));

      const splitName = (fullName = "") => {
        const parts = fullName.trim().split(/\s+/);
        return {
          first: parts[0] || "",
          last: parts.length > 1 ? parts.slice(1).join(" ") : "",
        };
      };

      const { first, last } = splitName(address.fullName);

      const payload = {
        first_name: first,
        last_name: last,
        address: [address.country, address.cityName, address.hood].filter(Boolean).join(", "),
        country: address.country || "",
        city: address.cityName || "",
        district: address.hood || "",
        street: address.street || "",
        total_price: Number(grandTotal),
        total_quantity: totalQuantity,
        coupon_code: coupon || undefined,
        items: itemsForPayload,
        currency: "SAR",
        // NEW: product_details as JSON string
        product_details: productDetails,
      };

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/carts/${cartId}/pay/edfapay`,
        payload,
        { headers }
      );

      const redirectUrl = data?.redirect_url || data?.payment?.redirect_url || null;

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      await swalInfo("تنبيه", data?.message || "تعذّر بدء عملية الدفع (لا يوجد رابط تحويل).");
    } catch (err) {
      console.error("Confirm (edfapay) failed:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "حدث خطأ أثناء بدء عملية الدفع.";
      await swalError("خطأ", msg);
    } finally {
      setConfirming(false);
    }
  };

  /* -------------------------- expose -------------------------- */
  return {
    // state
    items, cartId, address, payment, coupon, deletingId, confirming, cartLoading,
    guestPhone, guestLoaded, guestLoading, guestError,
    // setters
    setAddress, setPayment, setCoupon, setGuestPhone,
    // actions
    fetchGuestCart: fetchLocalGuestCart,
    inc, dec, handleRemove, handleConfirm,
    // computed
    partsTotal, deliveryTotalRaw, accessoriesTotal, discountTotal, deliverySurcharge, grandTotal,
    totalQuantity,
    // helpers
    money, ACCESSORY_FEE,
    // UI helper for the button
    guestReady,
  };
}
